import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Group } from "../../../types/group";
import { getGroupById, updateGroup } from "../../../services/groups";
import MembersInput from "./components/MembersInput";
import StationsMultiSelect from "./components/StationsMultiSelectOps";

import "./GroupEdit.css";
import TariffEditor from "./components/TariffEditor";

const GroupEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isTariffValid, setIsTariffValid] = useState(true);

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        const data = await getGroupById(id!);
        if (!data) throw new Error("Not found");
        setGroup(data as Group);
      } catch {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load group.",
          life: 4000,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchGroup();
  }, [id]);

  const handleAddUser = useCallback(
    (email: string) => {
      if (group!.members.some((m) => m.email === email)) {
        toast.current?.show({
          severity: "warn",
          summary: "Duplicate",
          detail: "This user is already in the group.",
          life: 3000,
        });
        return;
      }
      setGroup((prev) => ({
        ...prev!,
        members: [...prev!.members, { email, emaid: "" }],
      }));
    },
    [group],
  );

  const handleRemoveUser = useCallback((email: string) => {
    setGroup((prev) => ({
      ...prev!,
      members: prev!.members.filter((m) => m.email !== email),
    }));
  }, []);

  const handleSave = async () => {
    (document.activeElement as HTMLElement)?.blur();
    await new Promise((r) => setTimeout(r, 0));
    if (!group!.name.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Validation",
        detail: "Group name is required.",
        life: 3000,
      });
      return;
    }

    setSaving(true);
    try {
      await updateGroup(group!.groupId, {
        name: group!.name,
        stationIds: group!.stationIds,
        memberIdentifiers: group!.members.map((m) => ({ email: m.email })),
        tariff: group!.tariff,
      });
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: "Group updated successfully.",
        life: 2000,
      });
      setTimeout(() => navigate(`/target-groups/${group!.groupId}`), 1500);
    } catch (err: any) {
      const data = err?.response?.data;
      let detail = "Failed to save group.";
      if (data?.error_code === "member_is_station_owner_or_manager") {
        const { email, station_id } = data.error_args ?? {};
        detail = `${email} is already an owner or manager on station ${station_id} and cannot be added to this group.`;
      }
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail,
        life: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="group-edit-empty">
        <p>Group not found.</p>
        <Button
          label="Back to groups"
          icon="pi pi-arrow-left"
          text
          onClick={() => navigate("/target-groups")}
        />
      </div>
    );
  }

  return (
    <>
      <Toast
        ref={toast}
        position="top-right"
        appendTo={typeof window !== "undefined" ? document.body : undefined}
      />

      {/* Hero */}
      <div className="group-edit-hero">
        <div className="flex justify-content-between align-items-center">
          <div>
            <h1 className="group-edit-hero-title">Edit Group</h1>
            <div className="group-edit-hero-sub">
              <i className="pi pi-id-card" />
              {group.groupId}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              outlined
              style={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)" }}
              onClick={() => navigate(`/target-groups/${group.groupId}`)}
            />
            <Button
              label="Save changes"
              icon="pi pi-check"
              loading={saving}
              disabled={!isTariffValid}
              style={{
                background: "#c3e76b",
                color: "#1a3d35",
                border: "none",
                fontWeight: 700,
              }}
              onClick={handleSave}
            />
          </div>
        </div>
      </div>

      <div className="group-edit-grid">
        {/* Group Name */}
        <div className="group-edit-panel">
          <p className="group-edit-section-title">Group Name</p>
          <InputText
            value={group.name}
            onChange={(e) =>
              setGroup((prev) => ({ ...prev!, name: e.target.value }))
            }
            placeholder="Enter group name"
            className="w-full"
          />
        </div>

        {/* Members */}
        <div className="group-edit-panel">
          <p className="group-edit-section-title">Members</p>
          <MembersInput
            members={group.members.map((m) => m.email)}
            stationIds={group.stationIds}
            excludeGroupId={group.groupId}
            onAdd={handleAddUser}
            onRemove={handleRemoveUser}
          />
        </div>

        {/* Stations */}
        <div className="group-edit-panel group-edit-panel--full">
          <p className="group-edit-section-title">Stations</p>
          <StationsMultiSelect
            value={group.stationIds}
            onChange={(stationIds) =>
              setGroup((prev) => ({ ...prev!, stationIds }))
            }
          />
        </div>

        {/* Tariff */}
        {group.tariff && (
          <div className="group-edit-panel group-edit-panel--full">
            <p className="group-edit-section-title">Active Tariff</p>
            <TariffEditor
              tariff={group.tariff}
              onChange={(tariff) => setGroup((prev) => ({ ...prev!, tariff }))}
              onValidChange={setIsTariffValid}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default GroupEdit;
